import { Fragment } from 'react';
import './notes.css';

// Renders a comment body. Two formats coexist in sale_notes and both must read
// correctly here, exactly as they do in the admin portal's SaleCommentItem:
//   · content_json — TipTap document (everything written since rich text landed)
//   · content      — legacy plain text with @[Name](id) and /receipt[Label](id)
// Anything that can't be recognised falls through to plain text rather than
// rendering blank.

const RESOURCE_ICON = {
  receipt: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 6v12M9 9h4.5a1.8 1.8 0 0 1 0 3.6h-3a1.8 1.8 0 0 0 0 3.6H15" /></svg>
  ),
  attachment: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 11l-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L16 6" /></svg>
  ),
};

function Resource({ id, label, type, onResourceClick }) {
  const kind = type === 'attachment' ? 'attachment' : 'receipt';
  return (
    <span
      className={`note-resource ${kind}`}
      role="button"
      tabIndex={0}
      onClick={() => onResourceClick?.(kind, id)}
      onKeyDown={(e) => e.key === 'Enter' && onResourceClick?.(kind, id)}
    >
      {RESOURCE_ICON[kind]} {label}
    </span>
  );
}

/** Recursively render one TipTap JSON node. */
function renderNode(node, index, onResourceClick) {
  if (!node) return null;

  if (node.type === 'text') {
    let el = node.text;
    for (const mark of node.marks || []) {
      switch (mark.type) {
        case 'bold': el = <strong>{el}</strong>; break;
        case 'italic': el = <em>{el}</em>; break;
        case 'underline': el = <u>{el}</u>; break;
        case 'strike': el = <s>{el}</s>; break;
        case 'code': el = <code>{el}</code>; break;
        case 'link':
          el = <a href={mark.attrs?.href} target="_blank" rel="noopener noreferrer">{el}</a>;
          break;
        default: break;
      }
    }
    return <Fragment key={index}>{el}</Fragment>;
  }

  if (node.type === 'mention') {
    return <span key={index} className="note-mention">@{node.attrs?.label || 'Unknown'}</span>;
  }

  if (node.type === 'resourceMention') {
    return (
      <Resource
        key={index}
        id={node.attrs?.id}
        label={node.attrs?.label}
        type={node.attrs?.type}
        onResourceClick={onResourceClick}
      />
    );
  }

  const children = node.content?.map((child, i) => renderNode(child, i, onResourceClick)) || null;

  switch (node.type) {
    case 'doc': return <Fragment key={index}>{children}</Fragment>;
    case 'paragraph': return <p key={index}>{children || <br />}</p>;
    case 'bulletList': return <ul key={index}>{children}</ul>;
    case 'orderedList': return <ol key={index}>{children}</ol>;
    case 'listItem': return <li key={index}>{children}</li>;
    case 'blockquote': return <blockquote key={index}>{children}</blockquote>;
    case 'codeBlock': return <pre key={index}><code>{children}</code></pre>;
    case 'hardBreak': return <br key={index} />;
    default: return children ? <Fragment key={index}>{children}</Fragment> : null;
  }
}

const LEGACY_RE = /(@\[([^\]]+)\]\(([^)]+)\))|(\/(?:receipt|attachment)\[([^\]]+)\]\(([^)]+)\))/g;

/** Legacy plain text: @[Name](id) → mention chip, /receipt[Label](id) → resource chip. */
function renderLegacy(content, onResourceClick) {
  if (!content) return null;
  const parts = [];
  let last = 0;
  let m;
  LEGACY_RE.lastIndex = 0;
  while ((m = LEGACY_RE.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    if (m[1]) {
      parts.push(<span key={m.index} className="note-mention">@{m[2]}</span>);
    } else {
      const kind = m[4].startsWith('/receipt') ? 'receipt' : 'attachment';
      parts.push(<Resource key={m.index} id={m[6]} label={m[5]} type={kind} onResourceClick={onResourceClick} />);
    }
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts.length ? parts : content;
}

export default function NoteContent({ note, onResourceClick }) {
  if (note.is_deleted) return null;

  if (note.content_json?.content?.length > 0) {
    return <div className="note-content">{renderNode(note.content_json, 0, onResourceClick)}</div>;
  }
  return <div className="note-content"><p>{renderLegacy(note.content || note.body, onResourceClick)}</p></div>;
}

/** Turn a legacy plain-text body into TipTap JSON so it can be edited as rich text. */
export function legacyToTipTap(content) {
  if (!content) return null;
  const nodes = [];
  let last = 0;
  let m;
  LEGACY_RE.lastIndex = 0;
  while ((m = LEGACY_RE.exec(content)) !== null) {
    if (m.index > last) nodes.push({ type: 'text', text: content.slice(last, m.index) });
    if (m[1]) {
      nodes.push({ type: 'mention', attrs: { id: m[3], label: m[2] } });
    } else {
      nodes.push({
        type: 'resourceMention',
        attrs: { id: m[6], label: m[5], type: m[4].startsWith('/receipt') ? 'receipt' : 'attachment' },
      });
    }
    last = m.index + m[0].length;
  }
  if (last < content.length) nodes.push({ type: 'text', text: content.slice(last) });

  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: nodes.length ? nodes : [{ type: 'text', text: content }] }],
  };
}
